import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { Formik, Form } from "formik";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { ActionSection, InputGroup, ModalHeader, PrimaryAction, ReplicateContent, ReplicateHeader, ReplicateSection, ReplicateSelectionCard, ReplicateSubtitle, ReplicateTitle, ScrollableContent, SideCancelBtn, SubmitBtn, Wrapper } from "../Styles/InputStyles";

const TellySettingsModal = ({
  onClose,
  settingsData,
  editSettingsMutate,
  editSettingsLoading,
  isLoading,
  isDataLoaded,
  setIsEditing,
  isEditing,
  syncTallyDataMutate,
  syncTallyDataLoading,
}) => {
  const [height, setHeight] = useState(window.innerHeight);
  const [isTallyEnabled, setIsTallyEnabled] = useState(false);
  useEffect(() => {
    if (settingsData) {
      setIsTallyEnabled(!!settingsData.isTallyEnabled);
    }
  }, [settingsData]);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const initialValues = {
    ip: settingsData?.ip || "",
    port: settingsData?.port || "",
    companyName: settingsData?.companyName || "",
    billRefPrefix: settingsData?.billRefPrefix || "",
    isTallyEnabled: settingsData?.isTallyEnabled || false,
  };

  useEffect(() => {
    if (settingsData) {
      setIsTallyEnabled(!!settingsData.isTallyEnabled);
    }
  }, [settingsData]);

  const handleTallyToggle = () => {
    const newValue = !isTallyEnabled;

    // Optimistic UI update
    setIsTallyEnabled(newValue);

    const payload = {
      ip: settingsData?.ip,
      port: settingsData?.port,
      companyName: settingsData?.companyName,
      billRefPrefix: settingsData?.billRefPrefix,
      isTallyEnabled: newValue ? 1 : 0, // ⚠️ change if API expects boolean
    };

    editSettingsMutate(payload, {
      onError: () => {
        // rollback if API fails
        setIsTallyEnabled(!newValue);
      },
    });
  };

  const handleSync = () => {
    if (syncTallyDataMutate) {
      syncTallyDataMutate({});
    }
  };
  return (
    <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <ModalHeader>
        <HeaderTitle>{isEditing ? "Edit Tally Settings" : "Tally Settings"}</HeaderTitle>
        <CloseButton onClick={onClose}>
          <MdClose color="white" size={24} />
        </CloseButton>
      </ModalHeader>

      {isLoading || !isDataLoaded ? (
        <div className="flex items-center justify-center h-full">
          <CircularProgress />
        </div>
      ) : (
        <>
          <ContentContainer>
            <div className={isEditing ? "px-6 py-0" : "px-6 pt-6 pb-20"}>
              <Section>

                <>
                  <SettingsGrid>
                    <SettingCard>
                      <SectionHeader>
                        <SectionTitle>Tally Integration Details</SectionTitle>
                      </SectionHeader>

                      <SettingRow>
                        <Label>IP Address</Label>
                        <Value>{settingsData?.ip || "Not configured"}</Value>
                      </SettingRow>

                      <SettingRow>
                        <Label>Port</Label>
                        <Value>{settingsData?.port || "Not configured"}</Value>
                      </SettingRow>

                      <SettingRow>
                        <Label>Company Name</Label>
                        <Value>{settingsData?.companyName || "Not configured"}</Value>
                      </SettingRow>

                      <SettingRow>
                        <Label>Bill Reference Prefix</Label>
                        <Value>{settingsData?.billRefPrefix || "Not configured"}</Value>
                      </SettingRow>

                    </SettingCard>
                  </SettingsGrid>


                  {/* Sync Button Section */}
                  <ReplicateSection className="p-5 rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all">

                    {/* HEADER */}
                    <ReplicateHeader className="mb-4">
                      <ReplicateTitle className="text-base font-semibold text-gray-900">
                        Sync with Tally
                      </ReplicateTitle>

                      <ReplicateSubtitle className="text-sm text-gray-500 mt-1">
                        Manually sync bookings, payments and invoices with Tally.
                      </ReplicateSubtitle>
                    </ReplicateHeader>

                    {/* STATUS ROW */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-gray-500">
                        Last synced
                      </span>

                      <span className="text-xs font-medium text-gray-700">
                        {settingsData?.lastSync
                          ? settingsData.lastSync
                          : "Never"}
                      </span>
                    </div>

                    {/* CTA */}
                    <ReplicateContent>
                      <PrimaryAction
                        onClick={handleSync}
                        disabled={!isTallyEnabled || syncTallyDataLoading}
                        className={`
                        w-full flex items-center justify-center gap-2
                        border border-primary-color text-primary-color
                        bg-transparent
                        hover:bg-primary-color/5
                        transition-all duration-200
                        ${!isTallyEnabled ? "opacity-50 cursor-not-allowed border-gray-300 text-gray-400" : ""}
                      `}
                      >
                        {syncTallyDataLoading ? (
                          <>
                            <CircularProgress size={16} color="inherit" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            Sync with Tally
                          </>
                        )}
                      </PrimaryAction>


                    </ReplicateContent>
                  </ReplicateSection>
                </>
                {/* )} */}

                {!isTallyEnabled && (
                  <p className="text-xs text-gray-500 mt-2 text-center leading-relaxed">
                    Tally integration is currently not enabled to your account.
                    Reach out to{" "}
                    <span className="text-primary-color font-medium">
                      pallav@think-straight.com
                    </span>{" "}
                    to enable it for your account.
                  </p>
                )}
              </Section>
            </div>
          </ContentContainer>
        </>
      )}
    </Container>
  );
};
const Toggle = ({ checked, onChange, disabled }) => (
  <ToggleLabel disabled={disabled}>
    <ToggleInput
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <ToggleSlider checked={checked} disabled={disabled}>
      <ToggleThumb checked={checked} />
    </ToggleSlider>
  </ToggleLabel>
);
const ToggleLabel = tw.label`${props => props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative inline-flex items-center`;
const ToggleInput = tw.input`sr-only peer`;
const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.disabled ? 'bg-gray-200 cursor-not-allowed' : props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleThumb = tw.div`absolute top-0.5 left-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${props => props.checked ? 'transform translate-x-6' : ''}`;

const SettingContent = tw.div`
  flex items-center justify-between gap-4
`;
const SettingHeader = tw.div`flex-1`;
const TitleRow = tw.div`
  flex items-center gap-2 mb-1
`;
const SettingTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;
const SettingDescription = tw.p`
  text-xs text-gray-500 leading-relaxed
`;
const ToggleWrapper = tw.div`
  flex items-center justify-center
`;


// Styled Components
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] p-4`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const Section = tw.div`space-y-6`;

const SettingsGrid = tw.div`space-y-5`;

const SettingCard = tw.div`
  bg-white rounded-xl border border-gray-200 
  shadow-sm hover:shadow-md transition-all duration-200
`;

const SectionHeader = tw.div`
  px-5 pt-5 pb-3 border-b border-gray-100
`;

const SectionTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;

const SettingRow = tw.div`
  flex items-center justify-between 
  px-5 py-3 border-b border-gray-100 last:border-0
`;

const Label = tw.span`
  text-sm text-gray-500
`;

const Value = tw.span`
  text-sm font-semibold text-gray-900
`;

const Input = tw.input`border border-gray-300 rounded h-10 px-2 py-1 focus:ring-1 focus:ring-primary-color focus:border-primary-color focus:outline-none`;
const Error = tw.p`text-rose-600 text-sm`;

const Checkbox = tw.input`
  mr-2 w-4 h-4 text-primary-color 
  focus:ring-primary-color border-gray-300 rounded
`;
const SyncSection = tw.div`
  mt-6 p-5 bg-blue-50 rounded-2xl border border-blue-100
`;

const SyncHeader = tw.div`
  mb-4
`;

const SyncTitle = tw.h4`
  text-base font-semibold text-gray-800 mb-1
`;

const SyncSubtitle = tw.p`
  text-sm text-gray-500
`;

const SyncContent = tw.div`
  flex flex-col gap-3
`;

const SyncButton = tw.button`
  w-full bg-blue-600 text-white font-medium py-3 px-4 
  rounded-xl hover:bg-blue-700 transition-all duration-200
  disabled:opacity-50 disabled:cursor-not-allowed
  flex items-center justify-center
`;
export default TellySettingsModal;